import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { sendLoginOTP, verifyLoginOTP } from '../apiClient';
import { useAuth } from '../AuthContext';
import Spinner from '../components/common/Spinner';
import AuthShell from '../components/marketing/AuthShell';
import { Button } from '../components/ui';

function Login() {
  const [email, setEmail] = useState(''); // Email input
  const [otp, setOtp] = useState(''); // OTP input
  const [error, setError] = useState(null); // Error message state
  const [message, setMessage] = useState(null); // Success message state
  const [showOtpInput, setShowOtpInput] = useState(false); // Toggle for showing OTP input
  const [isLoading, setIsLoading] = useState(false); // Loading state for disabling buttons
  const [isCheckingAuth, setIsCheckingAuth] = useState(true); // Loading state for auth check
  const [attemptsRemaining, setAttemptsRemaining] = useState(null); // OTP attempts remaining
  const { setIsAuthenticated, checkAuthStatus, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuthentication = async () => {
      await checkAuthStatus();
      setIsCheckingAuth(false);
    };
    checkAuthentication();
  }, [checkAuthStatus]);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setAttemptsRemaining(null);
    setIsLoading(true);
  
    const { data, error } = await sendLoginOTP(email.toLowerCase());
  
    setIsLoading(false);
    if (error) {
      setError(error);
    } else if (data?.success) {
      setMessage(data.message || 'Login code sent to your email address');
      setShowOtpInput(true);
    } else {
      setError('Failed to send login code. Please try again.');
    }
  };
  

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setIsLoading(true);

    const { data, error } = await verifyLoginOTP(email.toLowerCase(), otp);
    
    if (error) {
      setError(error);
    } else if (data?.message === 'Login successful') {
      // Store session data
      localStorage.setItem('session', JSON.stringify(data.session));
      localStorage.setItem('user', JSON.stringify(data.user));
      
      await checkAuthStatus();
      setMessage('Login successful!');
      
      // Handle redirect based on user role
      setTimeout(() => {
        if (data.redirect) {
          if (data.redirect.type === 'organization') {
            navigate(`/organization/${data.redirect.id}`);
          } else if (data.redirect.type === 'vendor') {
            navigate(`/vendor/${data.redirect.id}`);
          } else {
            navigate('/');
          }
        } else {
          navigate('/');
        }
      }, 1000);
    } else if (data?.attemptsRemaining !== undefined) {
      setAttemptsRemaining(data.attemptsRemaining);
      setError(data.message || 'Invalid OTP');
    } else {
      setError(data?.message || 'Login failed. Please try again.');
    }
    
    setIsLoading(false);
  };

  useEffect(() => {
    if (isAuthenticated && isAuthenticated.authenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  if (isCheckingAuth) {
    return <Spinner />;
  }

  return (
    <AuthShell
      title="Welcome back"
      subtitle={showOtpInput ? 'Enter the code we emailed you.' : 'Sign in to view your tickets and passes.'}
      side={{
        heading: 'Your tickets, always on your phone.',
        sub: 'Sign in to access your mobile tickets, passes, and account.',
        bullets: ['Instant mobile QR tickets', 'No app to download', 'One login for every school'],
      }}
    >
      {error && <p className="mb-4 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 text-center">{error}</p>}
      {message && <p className="mb-4 text-sm text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2 text-center">{message}</p>}
      {attemptsRemaining !== null && (
        <p className="mb-4 text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2 text-center">
          {attemptsRemaining} attempt{attemptsRemaining !== 1 ? 's' : ''} remaining
        </p>
      )}

      <form onSubmit={showOtpInput ? handleVerifyOtp : handleSendOtp} className="space-y-4">
        {!showOtpInput ? (
          <input
            type="email" placeholder="Email address" value={email}
            onChange={(e) => setEmail(e.target.value)} required
            className="w-full px-4 py-3 rounded-xl border border-ink-200 outline-none focus:ring-2 focus:ring-brand-300"
          />
        ) : (
          <input
            type="text" placeholder="Enter the 6-digit code" value={otp}
            onChange={(e) => setOtp(e.target.value)} required
            className="w-full px-4 py-3 rounded-xl border border-ink-200 outline-none focus:ring-2 focus:ring-brand-300 tracking-widest text-center"
          />
        )}
        <Button type="submit" size="lg" className="w-full" disabled={isLoading}>
          {isLoading ? 'Loading...' : showOtpInput ? 'Verify code' : 'Send code'}
        </Button>
      </form>

      {showOtpInput && (
        <button onClick={() => setShowOtpInput(false)} className="w-full mt-4 text-sm text-brand-600 hover:text-brand-700 font-medium">
          ← Back to email
        </button>
      )}

      <p className="text-center mt-6 text-sm text-ink-500">
        Are you a vendor or district?{' '}
        <Link to="/vendor-login" className="text-brand-600 font-medium hover:underline">Vendor login</Link>
      </p>
    </AuthShell>
  );
}

export default Login;
